import { useTranslation } from 'i18n';
import { logger } from 'monitor';
import { ConfirmationDialog, Menu, MenuItem, Stack, Switch, Typography, useConfirm } from 'ui';
import { Close, FlashOffOutlined, FlashOnOutlined, SettingsOutlined } from 'ui/icons';

import { NetworkOverrideSwitch } from '../../../network-status';
import { DarkIconButton, Root } from './styles';

const SettingsMenu = ({ isPortrait }: { isPortrait: boolean }) => {
  const { t } = useTranslation();

  return (
    <Menu
      anchorOrigin={{
        horizontal: 'right',
        vertical: isPortrait ? 'bottom' : 'top',
      }}
      disableScrollLock
      transformOrigin={{
        horizontal: 'right',
        vertical: isPortrait ? 'top' : 'bottom',
      }}
      equalMenuWidth={false}
      button={
        <DarkIconButton aria-label={t('features.cameraGuidance.cameraHeader.settings.title')}>
          <SettingsOutlined />
        </DarkIconButton>
      }
    >
      <Stack
        sx={{
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="body1">{t('features.cameraGuidance.cameraHeader.settings.title')}</Typography>
      </Stack>

      <MenuItem>
        <Stack>
          <NetworkOverrideSwitch />

          <Typography
            variant="body2"
            color="secondary"
            sx={{
              textWrap: 'wrap',
            }}
          >
            {t('features.cameraGuidance.cameraHeader.settings.description')}
          </Typography>
        </Stack>
      </MenuItem>
    </Menu>
  );
};

export const CameraHeader = ({
  autoCapturing,
  toggleAutoCapture,
  handleStopCamera,
  isPortrait,
  handleCompleteCameraSession,
  completeDisabled,
  handleFlashToggle,
  flashEnabled,
  obfuscateAi,
}: {
  autoCapturing: boolean;
  toggleAutoCapture: () => void;
  handleStopCamera: () => void;
  isPortrait: boolean;
  handleCompleteCameraSession: () => void;
  completeDisabled?: boolean;
  handleFlashToggle: () => void;
  flashEnabled: boolean;
  obfuscateAi: boolean;
}) => {
  const { t } = useTranslation();
  const {
    open,
    handleCancel,
    handleClickOpen: openConfirmationDialog,
    handleConfirm,
    handleClose,
  } = useConfirm({
    onConfirm: handleCompleteCameraSession,
    onReject: handleStopCamera,
  });
  const handleCloseClick = () => {
    logger.info('Camera header close button clicked', {
      completeDisabled,
      isPortrait,
    });

    if (completeDisabled) {
      handleStopCamera();
      return;
    }

    openConfirmationDialog();
  };
  const handleAutoCaptureToggle = () => {
    logger.info('Camera header auto-capture switch changed', {
      autoCapturing,
      nextAutoCapturing: !autoCapturing,
      isPortrait,
    });
    toggleAutoCapture();
  };
  const handleFlashClick = () => {
    logger.info('Camera header flash toggle clicked', {
      flashEnabled,
      isPortrait,
      nextFlashEnabled: !flashEnabled,
    });
    handleFlashToggle();
  };
  return (
    <Root isPortrait={isPortrait}>
      <DarkIconButton onClick={handleCloseClick} aria-label={t('features.navigation.sidebar.closeAriaLabel')}>
        <Close />
      </DarkIconButton>
      <ConfirmationDialog
        open={open}
        handleDismiss={handleClose}
        handleCancel={handleCancel}
        handleConfirm={handleConfirm}
        title={t('features.cameraGuidance.cameraHeader.confirmationDialog.title')}
        enableDismissButton={true}
        message={
          obfuscateAi
            ? t('features.cameraGuidance.cameraHeader.confirmationDialog.messageWithoutAiLabel')
            : t('features.cameraGuidance.cameraHeader.confirmationDialog.messageWithAiLabel')
        }
        slotProps={{
          cancelButton: {
            children: t('features.cameraGuidance.cameraHeader.confirmationDialog.actions.exitAnyway'),
            variant: 'secondary',
          },
          confirmButton: {
            children: t('features.cameraGuidance.cameraHeader.confirmationDialog.actions.complete'),
            disabled: completeDisabled === true,
          },
        }}
      />
      <Switch
        color="contrast"
        label={t('features.cameraGuidance.cameraHeader.autoCapture')}
        checked={autoCapturing}
        onChange={handleAutoCaptureToggle}
        sx={
          isPortrait
            ? {}
            : {
                transform: 'rotate(270deg)',
              }
        }
      />

      <DarkIconButton onClick={handleFlashClick} aria-label={t('features.cameraGuidance.cameraHeader.flashAriaLabel')}>
        {flashEnabled ? <FlashOnOutlined /> : <FlashOffOutlined />}
      </DarkIconButton>

      <SettingsMenu isPortrait={isPortrait} />
    </Root>
  );
};
